import { yupResolver } from "@hookform/resolvers/yup";
import { ApiServiceError, ApiError } from "libs/api";
import { useRefetchQuery } from "libs/reactQuery";
import { useGetVariantsByIds } from "modules/products";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "ui";

import {
  useAdProduct,
  useUpdateAdProduct,
  useUpsertAdProduct
} from "../../apis";
import {
  AdProductForm,
  AdProductFormValues
} from "../../components/forms/AdProductForm";
import { adProductValidationSchema } from "../../helpers/validations";
import { variantSummaryToPreview } from "../../helpers/variantAdapters";
import { AdProductModalProps } from "./AdProductModal.types";

const buildDefaultValues = (): AdProductFormValues => ({
  sourceId: "",
  productVariantIds: [],
  label: null,
  isActive: true
});

const extractMessage = (error: ApiServiceError<ApiError>): string => {
  const message = error.response?.data?.message;
  if (Array.isArray(message)) return message[0] ?? error.message;
  if (typeof message === "string") return message;
  return error.message;
};

const AdProductModal = ({
  isOpen,
  onClose,
  adProductId
}: AdProductModalProps) => {
  const { t } = useTranslation();
  const isEditing = Boolean(adProductId);

  const refetchList = useRefetchQuery(["whatsappAdProducts"]);
  const refetchOne = useRefetchQuery(["whatsappAdProduct", `${adProductId}`]);

  const { adProduct } = useAdProduct(adProductId);

  const initialVariantIds = useMemo(
    () => adProduct?.productVariantIds ?? [],
    [adProduct?.productVariantIds]
  );
  const { variants: initialVariants } = useGetVariantsByIds(initialVariantIds);
  const initialPreviews = useMemo(() => {
    const byId = new Map(initialVariants.map((v) => [v.id, v]));
    return initialVariantIds
      .map((id) => byId.get(id))
      .filter((v): v is (typeof initialVariants)[number] => Boolean(v))
      .map(variantSummaryToPreview);
  }, [initialVariants, initialVariantIds]);

  const defaultValues = buildDefaultValues();
  const editValues: AdProductFormValues | undefined = adProduct
    ? {
        sourceId: adProduct.sourceId,
        productVariantIds: adProduct.productVariantIds,
        label: adProduct.label,
        isActive: adProduct.isActive
      }
    : undefined;

  const methods = useForm<AdProductFormValues>({
    ...(isEditing && editValues
      ? { values: editValues }
      : { defaultValues }),
    resolver: yupResolver(adProductValidationSchema)
  });

  const handleMutationError = (error: ApiServiceError<ApiError>) => {
    const message = extractMessage(error);
    const name = error.response?.data?.error ?? "";

    if (
      name.includes("AdProductVariantNotFound") ||
      name.includes("AdProductVariantNotVisible") ||
      message.toLowerCase().includes("productvariantid")
    ) {
      methods.setError("productVariantIds", { message });
      return;
    }

    toast.error(message);
  };

  const {
    mutate: mutateUpsert,
    isLoading: isLoadingUpsert
  } = useUpsertAdProduct({
    onSuccess: () => {
      refetchList();
      toast.success(t("Ad mapping was saved successfully"));
      methods.reset();
      onClose();
    },
    onError: handleMutationError
  });

  const {
    mutate: mutateUpdate,
    isLoading: isLoadingUpdate
  } = useUpdateAdProduct({
    onSuccess: () => {
      refetchList();
      refetchOne();
      toast.success(t("Ad mapping was updated successfully"));
      methods.reset();
      onClose();
    },
    onError: handleMutationError
  });

  const onSubmit = (formData: AdProductFormValues) => {
    const trimmedLabel = formData.label?.trim() ?? "";
    const labelToSend = trimmedLabel.length > 0 ? trimmedLabel : null;

    if (isEditing && adProductId) {
      mutateUpdate({
        id: adProductId,
        body: {
          productVariantIds: formData.productVariantIds,
          label: labelToSend,
          isActive: formData.isActive
        }
      });
      return;
    }

    mutateUpsert({
      sourceId: formData.sourceId.trim(),
      productVariantIds: formData.productVariantIds,
      label: labelToSend ?? undefined
    });
  };

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={isOpen}
        title={isEditing ? t("Edit Ad Mapping") : t("New Ad Mapping")}
        okText={isEditing ? t("Save") : t("Create")}
        onClose={onClose}
        isLoading={isEditing ? isLoadingUpdate : isLoadingUpsert}
        onOk={methods.handleSubmit(onSubmit)}
      >
        <AdProductForm
          isEditing={isEditing}
          initialPreviews={initialPreviews}
        />
      </Modal>
    </FormProvider>
  );
};

export default AdProductModal;
